import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { BreachRefKind, BreachReferenceModel, BreachRowView, BreachSeverity } from '@lib/guarantees/data-access';
import { resolveGuarantee, resolvePrescription } from '@lib/guarantees/data-access/store/guarantees-v2.actions';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { ChipComponent } from '../common/chip.component';
import { ChipTone, SEVERITY_TONE } from '../common/guarantee-vocabulary';

const SEVERITY_ICONS: Record<BreachSeverity, string> = {
  Error: 'error',
  Warning: 'warning',
  Info: 'info',
};

/**
 * Reference kinds the UI can actually navigate to; the rest are shown as plain identifiers. An
 * appointment is deliberately not among them: a finding names one precisely because nothing links
 * to it yet, so there is no record to open — repairing that link is its own flow.
 */
const NAVIGABLE_KINDS: ReadonlySet<BreachRefKind> = new Set<BreachRefKind>([BreachRefKind.Guarantee, BreachRefKind.Prescription]);

/**
 * Analyser findings, each naming the objects it points at; those the timeline can reach are
 * navigable. Lives in the side panel — a band above the stream pushes the work down the page.
 */
@Component({
  selector: 'guarantees-breach-list',
  standalone: true,
  imports: [DatePipe, MatIcon, MatTooltip, TranslatePipe, ChipComponent],
  templateUrl: './breach-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreachListComponent {
  rows = input.required<BreachRowView[]>();

  private readonly store = inject(Store);

  /** Named for the template, which has no other way to say which kind a row was raised against. */
  protected readonly refKinds = BreachRefKind;

  protected readonly errorCount = computed(() => this.rows().filter(row => row.severity === BreachSeverity.Error).length);

  protected readonly warningCount = computed(() => this.rows().filter(row => row.severity === BreachSeverity.Warning).length);

  protected tone(severity: BreachSeverity): ChipTone {
    return SEVERITY_TONE[severity] ?? 'neutral';
  }

  protected icon(severity: BreachSeverity): string {
    return SEVERITY_ICONS[severity] ?? 'help';
  }

  protected isNavigable(reference: BreachReferenceModel): boolean {
    return NAVIGABLE_KINDS.has(reference.refKind);
  }

  /** Resolves through the store so a target outside the loaded window is fetched, not ignored. */
  protected openReference(reference: BreachReferenceModel): void {
    switch (reference.refKind) {
      case BreachRefKind.Guarantee:
        this.store.dispatch(resolveGuarantee({ guaranteeId: reference.id, track: true }));
        break;
      case BreachRefKind.Prescription:
        this.store.dispatch(resolvePrescription({ prescriptionId: reference.id, track: true }));
        break;
    }
  }

  protected openSource(row: BreachRowView): void {
    this.store.dispatch(resolveGuarantee({ guaranteeId: row.guaranteeId, track: true }));
  }
}
